import * as React from 'react';

/* ─────────────────────────────────────────────
 * XYQAR Client UI-Kit — unicorn-grade primitives
 * Dense, data-driven, no mock. Used across every
 * page of the client portal.
 * ──────────────────────────────────────────── */

export function PageTitle({ kicker, title, right }: { kicker: string; title: string; right?: React.ReactNode }) {
  return (
    <div className="mb-8 flex items-end justify-between gap-6">
      <div>
        <p className="text-[10px] tracking-[0.2em] text-white/40 uppercase font-mono">{kicker}</p>
        <h1
          className="text-3xl sm:text-4xl font-normal text-white mt-2"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {title}
        </h1>
      </div>
      {right}
    </div>
  );
}

export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-md bg-white/5 border border-white/10 p-6 ${className}`}>{children}</div>;
}

export function Btn({
  children, onClick, primary, disabled, type = 'button', as, href,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  primary?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  as?: 'a';
  href?: string;
}) {
  const cls =
    'inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold tracking-[0.15em] uppercase transition ' +
    (primary
      ? 'bg-white text-black hover:opacity-90'
      : 'bg-transparent text-white/70 border border-white/20 hover:border-white/50') +
    (disabled ? ' opacity-50 cursor-not-allowed' : '');
  if (as === 'a' && href) {
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={cls}>
      {children}
    </button>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: React.ReactNode }) {
  return (
    <Card className="text-center py-16">
      <p className="text-white/60 text-sm">{title}</p>
      {hint && <p className="text-white/30 text-xs mt-2">{hint}</p>}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </Card>
  );
}

/* ─── KPI tile ─── */
export function Kpi({
  label, value, hint, accent,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  accent?: 'green' | 'amber' | 'red' | 'blue';
}) {
  const dot =
    accent === 'green' ? 'bg-emerald-400' :
    accent === 'amber' ? 'bg-amber-400'   :
    accent === 'red'   ? 'bg-rose-400'    :
    accent === 'blue'  ? 'bg-sky-400'     : 'bg-white/40';
  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{label}</p>
      </div>
      <p className="text-3xl text-white mt-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{value}</p>
      {hint && <p className="text-[11px] text-white/40 mt-2 font-mono">{hint}</p>}
    </Card>
  );
}

/* ─── Fairness grade pill ─── */
const FAIRNESS_COLORS: Record<string, string> = {
  GREEN:  'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  YELLOW: 'bg-amber-500/15  text-amber-300  border-amber-500/40',
  ORANGE: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  RED:    'bg-rose-500/15   text-rose-300   border-rose-500/40',
};
export function FairnessPill({ grade }: { grade?: string }) {
  const g = (grade || 'YELLOW').toUpperCase();
  const cls = FAIRNESS_COLORS[g] ?? FAIRNESS_COLORS.YELLOW;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-mono uppercase tracking-[0.15em] ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {g}
    </span>
  );
}

/* ─── 8-dim breakdown ─── */
export function DimensionBars({ dims }: { dims: Record<string, number> | null | undefined }) {
  if (!dims) return <p className="text-white/40 text-xs italic">No breakdown available.</p>;
  const entries = Object.entries(dims).sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0));
  return (
    <div className="flex flex-col gap-2">
      {entries.map(([k, v]) => {
        const pct = Math.max(0, Math.min(100, Math.round(((v ?? 0) <= 1 ? (v ?? 0) * 100 : (v ?? 0)))));
        return (
          <div key={k} className="flex items-center gap-3">
            <span className="w-32 shrink-0 text-[10px] uppercase tracking-[0.15em] text-white/50 font-mono">{k}</span>
            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden border border-white/10">
              <div className="h-full bg-gradient-to-r from-white/60 to-white" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-10 text-right text-xs text-white font-mono">{pct}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Score chip (Wolf score 0-100) ─── */
export function ScoreChip({ score }: { score?: number | null }) {
  const v = Math.round(((score ?? 0) <= 1 ? (score ?? 0) * 100 : (score ?? 0)));
  const tier =
    v >= 85 ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' :
    v >= 70 ? 'bg-sky-500/15     text-sky-300     border-sky-500/40' :
    v >= 50 ? 'bg-amber-500/15   text-amber-300   border-amber-500/40' :
              'bg-white/5        text-white/60    border-white/10';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-mono ${tier}`}>
      <span className="text-[10px] uppercase tracking-[0.15em] opacity-70">Wolf</span>
      <span className="font-bold">{v}</span>
    </span>
  );
}

/* ─── Live status pill (audit, sla) ─── */
export function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-mono uppercase tracking-[0.15em] ${
      ok ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
         : 'bg-rose-500/10    text-rose-300    border-rose-500/30'
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`} />
      {label}
    </span>
  );
}

/* ─── Pill (generic) ─── */
export function Pill({ children, tone = 'mute' }: { children: React.ReactNode; tone?: 'mute' | 'live' | 'warn' }) {
  const cls =
    tone === 'live' ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' :
    tone === 'warn' ? 'bg-amber-500/10   text-amber-300   border-amber-500/30' :
                      'bg-white/5        text-white/60    border-white/10';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-mono uppercase tracking-[0.15em] ${cls}`}>
      {children}
    </span>
  );
}

/* ─── Table primitives ─── */
export function Table({ children }: { children: React.ReactNode }) {
  return <div className="rounded-md border border-white/10 overflow-hidden bg-white/5"><table className="w-full text-sm">{children}</table></div>;
}
export function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <th className={`text-left text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono px-4 py-3 border-b border-white/10 ${className}`}>{children}</th>;
}
export function Td({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 text-white/80 align-middle ${className}`}>{children}</td>;
}

/* ─── Section header (denser) ─── */
export function Section({ title, hint, right, children }: { title: string; hint?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-sm uppercase tracking-[0.2em] text-white/80 font-mono">{title}</h2>
          {hint && <p className="text-[11px] text-white/40 mt-1">{hint}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

/* ─── Number formatter EU ─── */
export const fmt = (n: number | null | undefined, suffix = '') =>
  (n ?? 0).toLocaleString('fr-FR') + suffix;
